import { ArrowLeftIcon, PhotoIcon, UserCircleIcon, ArrowUpTrayIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_COUNTRY_LIST, GET_LOCATION_LIST } from 'graphql/common/queries'
import { ADD_LOCATION, UPDATE_LOCATION } from 'graphql/common/mutations'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { PhoneNumberUtil } from 'google-libphonenumber'
const phoneUtil = PhoneNumberUtil.getInstance()
const isPhoneValid = phone => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone))
    } catch (error) {
        return false
    }
}
const customIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // URL to a marker icon (example: a pin icon)
    iconSize: [35, 35], // Size of the icon
    iconAnchor: [17, 34], // Position to anchor the icon
    popupAnchor: [0, -34], // Position for popup to open
})

export default function LocationForm() {
    const bahrainCoordinates = [26.0667, 50.5577]
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [locationInfo, setLocationInfo] = useState(null)
    const [countries, setCountries] = useState([])
    const [selectedCountry, setSelectedCountry] = useState('')

    const handleBack = () => {
        navigate('/location')
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.country) {
                setCountries(data.country)
            }
        },
    })

    useEffect(() => {
        getCountryList({
            variables: { code: null },
        })
    }, [])

    const [getLocationById, { loading: locationListLoading, error: locationListError }] = useLazyQuery(
        GET_LOCATION_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchLocation.success && data.searchLocation.code === '200') {
                    setLocationInfo(data.searchLocation.locations[0])
                }
            },
        }
    )

    useEffect(() => {
        if (id) {
            getLocationById({
                variables: {
                    page: {
                        limit: 1,
                        offset: 0,
                    },
                    searchString: null,
                    searchLocationId: id,
                },
            })
        }
    }, [id])
    //  MUtation Add Location
    const [addNewLocation, { loading: addLoading }] = useMutation(ADD_LOCATION, {
        onCompleted: data => {
            if (data?.addLocation.code === '200' && data?.addLocation.success === true) {
                CreateLocationFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateLocationFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.addBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateLocationFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })
    // Mutation Update Boat
    const [updateLocation, { loading: updateLoading }] = useMutation(UPDATE_LOCATION, {
        onCompleted: data => {
            if (data?.updateLocation.code === '200' && data?.updateLocation.success === true) {
                CreateLocationFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateLocationFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.updateBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateLocationFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })
    // Form Validation
    const CreateLocationFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            location_name_en: locationInfo?.name.en || '',
            location_name_ar: locationInfo?.name.ar || '',
            address_area: locationInfo?.address.area || '',
            address_road_no: locationInfo?.address.road_no || '',
            address_unit_no: locationInfo?.address.unit_no || '',
            address_block_no: locationInfo?.address.block_no || '',
            address_landmark: locationInfo?.address.landmark || '',
            country_code: locationInfo?.country_details?.country_code || '',
            geo_lat: locationInfo?.geo.lat || '',
            geo_lng: locationInfo?.geo.lng || '',
            contact_email: locationInfo?.contact?.email || '',
            contact_phone_number: locationInfo?.contact?.phone_number || '',
        },
        validationSchema: Yup.object().shape({
            location_name_en: Yup.string().required('Location name is required'),
            location_name_ar: Yup.string().required('Location name is required'),
            address_area: Yup.string().required('Area is required'),
            address_road_no: Yup.string().required('Road number is required'),
            address_unit_no: Yup.string().required('Unit number is required'),
            address_block_no: Yup.string().required('Block number is required'),
            address_landmark: Yup.string().required('Landmark is required'),
            geo_lat: Yup.string().required('Latitude is required'),
            geo_lng: Yup.string().required('Longitude is required'),
            contact_email: Yup.string().email('Invalid email address').required('Email is required'),
            contact_phone_number: Yup.string()
                .required(t('Mobile number is required'))
                .test('is-valid-phone', t('Invalid phone number'), value => isPhoneValid(value)),
            country_code: Yup.string().required('Country is required'),

            // contact_phone_number: Yup.string()
            //     .required(t('Mobile number is required'))
            //     .test('is-valid-phone', t('Invalid phone number'), value => isPhoneValid(value)),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            submitLocation(values)
        },
    })
    // Submit Location
    const submitLocation = values => {
        const formattedData = {
            location: {
                name: {
                    en: values.location_name_en,
                    ar: values.location_name_ar,
                },
                address: {
                    unit_no: values.address_unit_no,
                    road_no: values.address_road_no,
                    block_no: values.address_block_no,
                    contact: null,
                    area: values.address_area,
                    landmark: values.address_landmark,
                    country_code: values.country_code,
                },
                geo: {
                    lat: values.geo_lat,
                    lng: values.geo_lng,
                },
                contact: {
                    email: values.contact_email,
                    phone_number: values.contact_phone_number.trim(),
                },
            },
            locationId: null,
        }
        if (id) {
            formattedData.locationId = id
            updateLocation({
                variables: formattedData,
            })
        } else {
            addNewLocation({
                variables: formattedData,
            })
        }
        console.log('formattedData', formattedData)
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateLocationFormValidation.isSubmitting}
                        onClick={CreateLocationFormValidation.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Location Name')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t(" Enter the location's name in English and Arabic")}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="location_name_en"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('English name')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="location_name_en"
                                        name="location_name_en"
                                        type="text"
                                        value={CreateLocationFormValidation.values.location_name_en}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.location_name_en &&
                                        CreateLocationFormValidation.errors.location_name_en && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.location_name_en}
                                            </p>
                                        )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="location_name_ar"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Arabic name')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="location_name_ar"
                                        name="location_name_ar"
                                        type="text"
                                        value={CreateLocationFormValidation.values.location_name_ar}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.location_name_ar &&
                                        CreateLocationFormValidation.errors.location_name_ar && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.location_name_ar}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Address Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t(' Detailed address, including area, road, unit, block, and nearby landmarks')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-6">
                                <label
                                    htmlFor="country_code"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Country')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <select
                                        id="country_code"
                                        name="country_code"
                                        value={CreateLocationFormValidation.values.country_code}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    >
                                        <option value="">{t('Select Country')}</option>
                                        {countries.map(country => (
                                            <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                                {country.country} ({country.country_ar})
                                            </option>
                                        ))}
                                    </select>
                                    {CreateLocationFormValidation.touched.country_code &&
                                        CreateLocationFormValidation.errors.country_code && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.country_code}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-6">
                                <label
                                    htmlFor="address_area"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Area Name')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="address_area"
                                        name="address_area"
                                        type="text"
                                        value={CreateLocationFormValidation.values.address_area}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.address_area &&
                                        CreateLocationFormValidation.errors.address_area && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.address_area}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="address_road_no"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Road Number')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="address_road_no"
                                        name="address_road_no"
                                        type="text"
                                        value={CreateLocationFormValidation.values.address_road_no}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.address_road_no &&
                                        CreateLocationFormValidation.errors.address_road_no && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.address_road_no}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="address_unit_no"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Unit Number')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="address_unit_no"
                                        name="address_unit_no"
                                        type="text"
                                        value={CreateLocationFormValidation.values.address_unit_no}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.address_unit_no &&
                                        CreateLocationFormValidation.errors.address_unit_no && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.address_unit_no}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="address_block_no"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Block Number')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="address_block_no"
                                        name="address_block_no"
                                        type="text"
                                        value={CreateLocationFormValidation.values.address_block_no}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.address_block_no &&
                                        CreateLocationFormValidation.errors.address_block_no && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.address_block_no}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-6">
                                <label
                                    htmlFor="address_landmark"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Landmark')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="address_landmark"
                                        name="address_landmark"
                                        type="text"
                                        value={CreateLocationFormValidation.values.address_landmark}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.address_landmark &&
                                        CreateLocationFormValidation.errors.address_landmark && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.address_landmark}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="address_landmark"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Contact Email')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="contact_email"
                                        name="contact_email"
                                        type="text"
                                        value={CreateLocationFormValidation.values.contact_email}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.contact_email &&
                                        CreateLocationFormValidation.errors.contact_email && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.contact_email}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="address_landmark"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Phone Number')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <PhoneInput
                                        name="contact_phone_number"
                                        defaultCountry="bh"
                                        value={CreateLocationFormValidation.values.contact_phone_number}
                                        onChange={phone => {
                                            console.log('phoneNumber', phone)
                                            // PassengersFormValidation.setFieldValue('phoneCountryCode', dialCode)
                                            CreateLocationFormValidation.setFieldValue('contact_phone_number', phone)
                                        }}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        className="block w-full py-1.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.contact_phone_number &&
                                        CreateLocationFormValidation.errors.contact_phone_number && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.contact_phone_number}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Coordinates')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t(' Precise latitude and longitude with a map for easy reference.')}
                            </p>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label htmlFor="geo_lat" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Latitude ')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="geo_lat"
                                        name="geo_lat"
                                        type="text"
                                        value={CreateLocationFormValidation.values.geo_lat}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.geo_lat &&
                                        CreateLocationFormValidation.errors.geo_lat && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.geo_lat}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-3">
                                <label htmlFor="geo_lng" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Longitude ')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="geo_lng"
                                        name="geo_lng"
                                        type="text"
                                        value={CreateLocationFormValidation.values.geo_lng}
                                        onChange={CreateLocationFormValidation.handleChange}
                                        onBlur={CreateLocationFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateLocationFormValidation.touched.geo_lng &&
                                        CreateLocationFormValidation.errors.geo_lng && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateLocationFormValidation.errors.geo_lng}
                                            </p>
                                        )}
                                </div>
                            </div>
                            {/* <div className="sm:col-span-6">
                                <div className="w-full max-w-4xl h-96 rounded-lg shadow-lg overflow-hidden">
                                    <MapContainer center={bahrainCoordinates} zoom={10} className="w-full h-full">
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        />
                                        <Marker position={bahrainCoordinates} icon={customIcon}>
                                            <Popup>
                                                <strong>Bahrain</strong>
                                                <br />
                                                Coordinates: 26.0667, 50.5577
                                            </Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateLocationFormValidation.isSubmitting}
                        type="submit"
                        onClick={CreateLocationFormValidation.handleSubmit}
                        // disabled={CreateUserFormValidation.isSubmitting}
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
